import { Chart } from 'chart.js';
import 'chartjs-plugin-labels';
import config from '../../config';
import { createRequestCache } from '../Page/HomePage/floorDeckRequestCache';

const yearlyTrendUtils = require('./yearlyTrendUtils');

export const homeService = {
    drawChartOee,
    drawChartProd,
    drawChartQloss,
    drawChartTechloss,
    drawChartOrgloss,
    fetchYearlyTrends,
    getFloorDeckSummary,
    fetchFloorDeckSummary,
    fetchFloorDeckTrends,
    clearFloorDeckCache,
    showLabels,
    clearAllCharts,
    drawInventoryChart
};

const floorDeckRequestCache = createRequestCache(60 * 1000);
const floorDeckTrendRequestCache = createRequestCache(60 * 1000);

function parseJsonResponse(response) {
    return response.text().then((text) => (text ? JSON.parse(text) : null));
}

function floorDeckKey(company, date) {
    return `${company}:${date}`;
}

function fetchFloorDeckSummary({ company, date, force = false }) {
    const key = floorDeckKey(company, date);
    const loader = () => fetch(`${config.apiUrl}/getFloorDeckSummary?company=${encodeURIComponent(company)}&date=${encodeURIComponent(date)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(parseJsonResponse);

    return force
        ? floorDeckRequestCache.get(key, loader, true)
        : floorDeckRequestCache.get(key, loader);
}

function getFloorDeckSummary({ company, date }) {
    const key = floorDeckKey(company, date);
    const loader = () => fetch(`${config.apiUrl}/getFloorDeckSummary?company=${encodeURIComponent(company)}&date=${encodeURIComponent(date)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then(parseJsonResponse);
    return floorDeckRequestCache.getStaleWhileRevalidate(key, loader);
}

function fetchFloorDeckTrends({ company, date }) {
    const month = date.slice(0, 7);
    const year = date.slice(0, 4);
    return floorDeckTrendRequestCache.get(`${company}:${month}`, () => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ machine: [], month }),
        };

        return Promise.all([
            fetch(`${config.apiUrl}/getMachineOEEByDate?company=${encodeURIComponent(company)}`, requestOptions)
                .then(parseJsonResponse)
                .catch(() => []),
            fetchYearlyTrends({ selectedYear: year, machineStr: '' }),
        ]).then(([oeeRows, yearlyMetrics]) => ({
            oeeRows: Array.isArray(oeeRows) ? oeeRows : [],
            losses: (yearlyMetrics || []).filter((metric) => metric.loss),
        }));
    });
}

function clearFloorDeckCache() {
    floorDeckRequestCache.clear();
    floorDeckTrendRequestCache.clear();
}

// Fetches the five yearly PE&P metrics in parallel and returns them shaped for
// the Aurora trend cards: [{ title, loss, months: [[monthLabel, value], ...] }].
// Endpoints and request methods mirror the individual drawChart* functions.
function fetchYearlyTrends(obj) {
    if (!localStorage.getItem('user')) return Promise.resolve([]);

    const accountId = JSON.parse(localStorage.getItem('user')).accountId;
    const query = `company=${accountId}&year=${obj.selectedYear}&machine=${obj.machineStr}`;
    const metrics = [
        { title: 'OEE (%)', loss: false, endpoint: 'getMachineOEEByMonth', method: 'POST', reportAverage: true },
        { title: 'Productivity (%)', loss: false, endpoint: 'getMachineProductivityByMonth', method: 'POST', reportAverage: true },
        { title: 'Quality Loss (%)', loss: true, endpoint: 'getOverallQLoss', method: 'POST' },
        { title: 'Technical Loss (%)', loss: true, endpoint: 'getOverallTechLoss', method: 'POST' },
        { title: 'Organization Loss (%)', loss: true, endpoint: 'getOverallOrgLoss', method: 'POST' },
    ];

    const selectedMachines = obj.machineStr
        ? obj.machineStr.split(',').filter(Boolean).map((machine) => ({ label: machine, value: machine }))
        : [];

    const getEmptyMonths = (metric) => metric.reportAverage
        ? yearlyTrendUtils.calculateMonthlyOee([])
        : yearlyTrendUtils.normalizeYearlyMonths([], 'percent');

    return Promise.all(
        metrics.map((m) => {
            const url = m.reportAverage
                ? `${config.apiUrl}/${m.endpoint}?company=${accountId}&year=${obj.selectedYear}`
                : `${config.apiUrl}/${m.endpoint}?${query}`;
            const requestOptions = {
                method: m.method,
                headers: { 'Content-Type': 'application/json' },
            };

            if (m.reportAverage) {
                requestOptions.body = JSON.stringify({ machine: selectedMachines });
            }

            return fetch(url, requestOptions)
                .then((response) => response.text())
                .then((text) => {
                    const data = text && JSON.parse(text);
                    const rows = Array.isArray(data) ? data : [];
                    const months = m.reportAverage
                        ? yearlyTrendUtils.calculateMonthlyOee(rows)
                        : yearlyTrendUtils.normalizeYearlyMonths(rows, 'percent');
                    return { title: m.title, loss: m.loss, months };
                })
                .catch(() => ({ title: m.title, loss: m.loss, months: getEmptyMonths(m) }));
        })
    );
}

function showLabels() {
    Chart.Tooltip.positioners.outer = function (elements) {
        if (!elements.length) {
            return false;
        }

        var i, len;
        var x = 0;
        var y = 0;

        for (i = 0, len = elements.length; i < len; ++i) {
            var el = elements[i];
            if (el && el.hasValue()) {
                var elPosX = el._view.x + 0.95 * el._view.outerRadius * Math.cos((el._view.endAngle - el._view.startAngle) / 2 + el._view.startAngle);
                var elPosY = el._view.y + 0.95 * el._view.outerRadius * Math.sin((el._view.endAngle - el._view.startAngle) / 2 + el._view.startAngle);
                if (x < elPosX) {
                    x = elPosX;
                }
                if (y < elPosY) {
                    y = elPosY;
                }
            }
        }

        return {
            x: Math.round(x),
            y: Math.round(y)
        };
    };

    Chart.plugins.register({
        afterDatasetsDraw: function (chartInstance, easing) {
            // Skip inventory charts (both occupancy and non-occupancy) - they have their own center text handling
            if (chartInstance.config.options && (chartInstance.config.options.isOccupancyChart || chartInstance.config.options.isInventoryChart)) {
                return;
            }
            if (chartInstance.config.type == "doughnut") {
                var ctx = chartInstance.chart.ctx;
                var sum = 0;
                var count = 0;
                chartInstance.data.datasets.forEach(function (dataset, i) {
                    var meta = chartInstance.getDatasetMeta(i);
                    if (!meta.hidden) {
                        meta.data.forEach(function (element, index) {
                            ctx.fillStyle = 'white';

                            var fontSize = 16;
                            var fontStyle = 'normal';
                            var fontFamily = 'Arial';
                            ctx.font = Chart.helpers.fontString(fontSize, fontStyle, fontFamily);

                            var dataString = chartInstance.data.labels[index];
                            var dataString2 = dataset.data[index];

                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';

                            var padding = 5;
                            var position = element.tooltipPosition();

                            ctx.fillText(dataString, position.x, position.y - (fontSize / 2) - padding); //month
                            ctx.fillText(dataString2, position.x, position.y - (fontSize / 2) - padding + fontSize); //value

                            // 円の中心に表示する合計を集計する
                            sum += Number(dataset.data[index]);
                            count++;
                        });
                    }
                });
                if(sum / count>=localStorage.getItem('target1'))
                ctx.fillStyle = '#228B22';
                else
                ctx.fillStyle = '#DC143C';
                var fontSize = 30;
                var fontStyle = 'bold';
                var fontFamily = "Arial";
                ctx.font = Chart.helpers.fontString(fontSize, fontStyle, fontFamily);

                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                var centerX = (chartInstance.chartArea.left + chartInstance.chartArea.right) / 2;
                var centerY = (chartInstance.chartArea.top + chartInstance.chartArea.bottom) / 2;
                if(sum==0)
                ctx.fillText('0', centerX, centerY); //center value
                else
                ctx.fillText((sum / count).toFixed(0).toString(), centerX, centerY); //center value
            }
        }
    });
}
function getData(key, dataset, title) {
    let pointBackgroundColors = [];
    for (let i = 0; i < dataset.length; i++) {
        if (title.indexOf('OEE') > -1) {
            pointBackgroundColors =['#1a1334','#26294a','#01545a','#017351','#03c383','#aad962','#fbbf45','#ef6a32','#ed0345','#a12a5e','#710162','#110141']
        }
        else if (title.indexOf('Productivity') > -1) {
            pointBackgroundColors =['#1a1334','#26294a','#01545a','#017351','#03c383','#aad962','#fbbf45','#ef6a32','#ed0345','#a12a5e','#710162','#110141']
        }
        else if (title.indexOf('Quality') > -1) {
            pointBackgroundColors =['#1a1334','#26294a','#01545a','#017351','#03c383','#aad962','#fbbf45','#ef6a32','#ed0345','#a12a5e','#710162','#110141']
        }
        else if (title.indexOf('Technical') > -1) {
            pointBackgroundColors =['#1a1334','#26294a','#01545a','#017351','#03c383','#aad962','#fbbf45','#ef6a32','#ed0345','#a12a5e','#710162','#110141']
        }
        else if (title.indexOf('Organization') > -1) {
            pointBackgroundColors =['#1a1334','#26294a','#01545a','#017351','#03c383','#aad962','#fbbf45','#ef6a32','#ed0345','#a12a5e','#710162','#110141']
        }
    }
    return {
        type: 'doughnut',
        data: {
            labels: key,
            datasets: [{
                data: dataset,
                backgroundColor: pointBackgroundColors,
                hoverBackgroundColor: "#C0C0C0"
            }]
        },
        options: {
            layout: {
                padding: {
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0
                }
            },
            title: {
                display: true,
                text: title
            },
            legend: {
                display: false
            },
            plugins: {
                labels: false
            }
            // plugins: {
            //     labels: {
            //         fontSize: 12,
            //         position: 'outside',
            //         outsidePadding: 10,
            //         textMargin: 4,
            //         render: (args) => {
            //             return `${args.label}: \n${args.value}%`;
            //         }
            //     }
            // },
            //showAllTooltips: true,
        },

    }
}

function clearAllCharts(){
    if (window.myChart1 != undefined) window.myChart1.destroy();
    if (window.myChart2 != undefined) window.myChart2.destroy();
    if (window.myChart3 != undefined) window.myChart3.destroy();
    if (window.myChart4 != undefined) window.myChart4.destroy();
    if (window.myChart5 != undefined) window.myChart5.destroy();
    
    // Clean up inventory charts and their tooltips
    const inventoryChartKeys = Object.keys(window).filter(key => key.startsWith('inventoryChart_'));
    inventoryChartKeys.forEach(key => {
        if (window[key]) {
            // Call cleanup function if it exists
            const cleanupKey = `${key}_cleanup`;
            if (window[cleanupKey] && typeof window[cleanupKey] === 'function') {
                window[cleanupKey]();
                delete window[cleanupKey];
            }
            window[key].destroy();
            delete window[key];
        }
    });
    
    // Remove any remaining inventory tooltips
    const tooltips = document.querySelectorAll('[id^="inventoryTooltip_"]');
    tooltips.forEach(tooltip => {
        if (tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
        }
    });
}

function drawChartOee(obj) {
    var ctx = document.getElementById("oee");
    let arrays = [];
    let keys = [];

    const requestOptions = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    };
    if (localStorage.getItem('user')) {
        fetch(`${config.apiUrl}/getOverallOEE?company=${JSON.parse(localStorage.getItem('user')).accountId}&year=${obj.selectedYear}&machine=${obj.machineStr}`, requestOptions)
            .then((response) => {
                return response.text().then(text => {
                    const data = text && JSON.parse(text);
                    return data;
                });
            })
            .then(result => {
                let machine = {};

                for (const i in result) {
                    const key = result[i].Months;
                    let valueMc = result[i].percent;
                    machine[key] = valueMc;
                    keys.push(key);
                    arrays.push(valueMc);
                    if (window.myChart1 != undefined)
                        window.myChart1.destroy();
                    window.myChart1 = new Chart(ctx, getData(keys, arrays, title));

                }
                if (result.length == 0) {
                    window.myChart1 = new Chart(ctx, getData([], [], title));
                }
            });
        let title = 'OEE (%)';
    };
}

function drawChartProd(obj) {
    var ctx = document.getElementById("prod");
    let arrays = [];
    let keys = [];

    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    if (localStorage.getItem('user')) {
        fetch(`${config.apiUrl}/getOverallPoductivity?company=${JSON.parse(localStorage.getItem('user')).accountId}&year=${obj.selectedYear}&machine=${obj.machineStr}`, requestOptions)
            .then((response) => {
                return response.text().then(text => {
                    const data = text && JSON.parse(text);
                    return data;
                });
            })
            .then(result => {
                let machine = {};

                for (const i in result) {
                    const key = result[i].Months;
                    let valueMc = result[i].percent;
                    machine[key] = valueMc;
                    keys.push(key);
                    arrays.push(valueMc);
                    if (window.myChart2 != undefined)
                        window.myChart2.destroy();
                    window.myChart2 = new Chart(ctx, getData(keys, arrays, title));

                }
                if (result.length == 0) {
                    window.myChart2 = new Chart(ctx, getData([], [], title));
                }
            });
        let title = 'Productivity (%)';
    }
}

function drawChartQloss(obj) {
    var ctx = document.getElementById("qloss");
    let arrays = [];
    let keys = [];
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    if (localStorage.getItem('user')) {
        fetch(`${config.apiUrl}/getOverallQLoss?company=${JSON.parse(localStorage.getItem('user')).accountId}&year=${obj.selectedYear}&machine=${obj.machineStr}`, requestOptions)
            .then((response) => {
                return response.text().then(text => {
                    const data = text && JSON.parse(text);
                    return data;
                });
            })
            .then(result => {
                let machine = {};

                for (const i in result) {
                    const key = result[i].Months;
                    let valueMc = result[i].percent;
                    machine[key] = valueMc;
                    keys.push(key);
                    arrays.push(valueMc);
                    if (window.myChart3 != undefined)
                        window.myChart3.destroy();
                    window.myChart3 = new Chart(ctx, getData(keys, arrays, title));

                }
                if (result.length == 0) {
                    window.myChart3 = new Chart(ctx, getData([], [], title));
                }
            });
        let title = 'Quality Loss (%)';
    }
}

function drawChartTechloss(obj) {
    var ctx = document.getElementById("techloss");
    let arrays = [];
    let keys = [];

    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    if (localStorage.getItem('user')) {
        fetch(`${config.apiUrl}/getOverallTechLoss?company=${JSON.parse(localStorage.getItem('user')).accountId}&year=${obj.selectedYear}&machine=${obj.machineStr}`, requestOptions)
            .then((response) => {
                return response.text().then(text => {
                    const data = text && JSON.parse(text);
                    return data;
                });
            })
            .then(result => {
                let machine = {};

                for (const i in result) {
                    const key = result[i].Months;
                    let valueMc = result[i].percent;
                    machine[key] = valueMc;
                    keys.push(key);
                    arrays.push(valueMc);
                    if (window.myChart4 != undefined)
                        window.myChart4.destroy();
                    window.myChart4 = new Chart(ctx, getData(keys, arrays, title));

                }
                if (result.length == 0) {
                    window.myChart4 = new Chart(ctx, getData([], [], title));
                }
            });
        let title = 'Technical Loss (%)';
    }
}

function drawChartOrgloss(obj) {
    var ctx = document.getElementById("orgloss");
    let arrays = [];
    let keys = [];

    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    if (localStorage.getItem('user')) {
        fetch(`${config.apiUrl}/getOverallOrgLoss?company=${JSON.parse(localStorage.getItem('user')).accountId}&year=${obj.selectedYear}&machine=${obj.machineStr}`, requestOptions)
            .then((response) => {
                return response.text().then(text => {
                    const data = text && JSON.parse(text);
                    return data;
                });
            })
            .then(result => {
                let machine = {};

                for (const i in result) {
                    const key = result[i].Months;
                    let valueMc = result[i].percent;
                    machine[key] = valueMc;
                    keys.push(key);
                    arrays.push(valueMc);
                    if (window.myChart5 != undefined)
                        window.myChart5.destroy();
                    window.myChart5 = new Chart(ctx, getData(keys, arrays, title));

                }
                if (result.length == 0) {
                    window.myChart5 = new Chart(ctx, getData([], [], title));
                }
            });
        let title = 'Organization Loss (%)';
    }
}

// Register plugin for inventory charts (only once) - using Chart.js v2 syntax
if (!Chart.plugins.getAll().find(p => p.id === 'inventoryCenterTextPlugin')) {
    Chart.plugins.register({
        id: 'inventoryCenterTextPlugin',
        afterDatasetsDraw: function(chartInstance) {
            const isOccupancyChart = chartInstance.config.options.isOccupancyChart;
            
            // Draw center legend for occupancy pies to avoid overlap on tiny slices
            const isInventoryChart = chartInstance.config.options.isInventoryChart;
            if (isInventoryChart && isOccupancyChart && chartInstance.config.type === 'doughnut') {
                const ctx = chartInstance.chart.ctx;
                const labels = chartInstance.data.labels || [];
                const dataset = (chartInstance.data.datasets && chartInstance.data.datasets[0]) ? chartInstance.data.datasets[0] : { data: [], backgroundColor: [] };
                const values = dataset.data || [];
                const colors = dataset.backgroundColor || [];
                const total = values.reduce((a, b) => Number(a) + Number(b), 0);

                const rows = labels.map((label, index) => {
                    const rawValue = Number(values[index] || 0);
                    if (rawValue <= 0) return null;
                    const pct = total > 0 ? ((rawValue / total) * 100).toFixed(1) : '0.0';
                    return {
                        text: `${label}: ${rawValue.toLocaleString()} (${pct}%)`,
                        color: colors[index] || '#666'
                    };
                }).filter(Boolean);

                if (rows.length > 0) {
                    const centerX = (chartInstance.chartArea.left + chartInstance.chartArea.right) / 2;
                    const centerY = (chartInstance.chartArea.top + chartInstance.chartArea.bottom) / 2;
                    const chartHeight = chartInstance.chartArea.bottom - chartInstance.chartArea.top;

                    const maxFontSize = 12;
                    const minFontSize = 8;
                    const availableHeight = Math.max(30, chartHeight * 0.42);
                    const lineHeight = Math.max(10, Math.floor(availableHeight / rows.length));
                    const fontSize = Math.max(minFontSize, Math.min(maxFontSize, lineHeight - 2));
                    const startY = centerY - ((rows.length - 1) * lineHeight) / 2;

                    ctx.save();
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'middle';
                    ctx.font = Chart.helpers.fontString(fontSize, 'bold', 'Arial');

                    rows.forEach((row, lineIndex) => {
                        const lineY = startY + (lineIndex * lineHeight);
                        const markerSize = Math.max(6, Math.floor(fontSize * 0.75));
                        const markerGap = 6;
                        const textWidth = ctx.measureText(row.text).width;
                        const rowWidth = markerSize + markerGap + textWidth;
                        const rowStartX = centerX - (rowWidth / 2);

                        // Color marker square beside each legend text
                        ctx.fillStyle = row.color;
                        ctx.fillRect(rowStartX, lineY - (markerSize / 2), markerSize, markerSize);

                        // Text
                        ctx.fillStyle = '#333';
                        ctx.fillText(row.text, rowStartX + markerSize + markerGap, lineY);
                    });
                    ctx.restore();
                }
            }
            
            // Draw center text for non-occupancy charts (including inventory totals)
            if (!isOccupancyChart && chartInstance.config.type === 'doughnut' && chartInstance.config.options.centerText && chartInstance.config.options.centerText !== '') {
                const ctx = chartInstance.chart.ctx;
                const centerX = (chartInstance.chartArea.left + chartInstance.chartArea.right) / 2;
                const centerY = (chartInstance.chartArea.top + chartInstance.chartArea.bottom) / 2;
                const centerText = chartInstance.config.options.centerText;
                const dataset = (chartInstance.data.datasets && chartInstance.data.datasets[0]) ? chartInstance.data.datasets[0] : { data: [], backgroundColor: [] };
                const labels = chartInstance.data.labels || [];
                const values = dataset.data || [];
                const colors = dataset.backgroundColor || [];
                
                // Split text at colon to put value on next line
                const colonIndex = centerText.indexOf(':');
                let labelText = centerText;
                let valueText = '';
                
                if (colonIndex !== -1) {
                    labelText = centerText.substring(0, colonIndex + 1); // Include the colon
                    valueText = centerText.substring(colonIndex + 1).trim(); // Value after colon
                }
                
                ctx.save();
                ctx.fillStyle = '#333';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                
                // Draw label (first line)
                ctx.font = 'bold 12px Arial';
                ctx.fillText(labelText, centerX, centerY + 5);
                
                // Draw value (second line)
                if (valueText) {
                    ctx.font = 'bold 14px Arial';
                    ctx.fillText(valueText, centerX, centerY + 20);
                } else {
                    // If no colon found, draw as single line
                    ctx.font = 'bold 13px Arial';
                    ctx.fillText(centerText, centerX, centerY + 12);
                }

                // For inventory total charts, add a compact legend below the center total
                if (isInventoryChart) {
                    const total = values.reduce((a, b) => Number(a) + Number(b), 0);
                    const rows = labels.map((label, index) => {
                        const rawValue = Number(values[index] || 0);
                        if (rawValue <= 0) return null;
                        const pct = total > 0 ? ((rawValue / total) * 100).toFixed(1) : '0.0';
                        return {
                            text: `${label}: ${rawValue.toLocaleString()} (${pct}%)`,
                            color: colors[index] || '#666'
                        };
                    }).filter(Boolean);

                    if (rows.length > 0) {
                        const chartHeight = chartInstance.chartArea.bottom - chartInstance.chartArea.top;
                        const availableHeight = Math.max(24, chartHeight * 0.20);
                        const lineHeight = Math.max(9, Math.floor(availableHeight / rows.length));
                        const fontSize = Math.max(7, Math.min(10, lineHeight - 1));
                        const startY = centerY + 36;

                        ctx.textAlign = 'left';
                        ctx.textBaseline = 'middle';
                        ctx.font = Chart.helpers.fontString(fontSize, 'bold', 'Arial');

                        rows.forEach((row, idx) => {
                            const y = startY + (idx * lineHeight);
                            const markerSize = Math.max(5, Math.floor(fontSize * 0.75));
                            const markerGap = 5;
                            const textWidth = ctx.measureText(row.text).width;
                            const rowWidth = markerSize + markerGap + textWidth;
                            const x = centerX - (rowWidth / 2);

                            ctx.fillStyle = row.color;
                            ctx.fillRect(x, y - (markerSize / 2), markerSize, markerSize);

                            ctx.fillStyle = '#333';
                            ctx.fillText(row.text, x + markerSize + markerGap, y);
                        });
                    }
                }
                
                ctx.restore();
            }
        }
    });
}

function drawInventoryChart(canvasId, title, data, centerText = '', isOccupancyChart = false) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) {
        // Retry after a short delay if canvas not found
        setTimeout(() => {
            const retryCanvas = document.getElementById(canvasId);
            if (retryCanvas) {
                drawInventoryChart(canvasId, title, data, centerText, isOccupancyChart);
            }
        }, 100);
        return;
    }
    
    // Destroy existing chart if it exists
    const chartKey = `inventoryChart_${canvasId}`;
    if (window[chartKey]) {
        // Call cleanup function if it exists
        const cleanupKey = `${chartKey}_cleanup`;
        if (window[cleanupKey] && typeof window[cleanupKey] === 'function') {
            window[cleanupKey]();
            delete window[cleanupKey];
        }
        window[chartKey].destroy();
    }
    
    // Calculate total
    const total = data.length > 0 ? data.reduce((sum, item) => sum + item.value, 0) : 0;
    
    // Handle empty data case - let Chart.js handle rendering
    if (total === 0 || data.length === 0) {
        return;
    }
    
    // Prepare labels and values, filter out zero values
    const filteredData = data.filter(item => item.value > 0);
    const labels = filteredData.map(item => item.label);
    const values = filteredData.map(item => item.value);
    
    // Use the same color palette as PE&P charts
    const colorPalette = ['#1a1334','#26294a','#01545a','#017351','#03c383','#aad962','#fbbf45','#ef6a32','#ed0345','#a12a5e','#710162','#110141'];
    const backgroundColor = labels.map((label, index) => {
        // Use specific colors for known labels
        if (label === 'Good Parts') return '#03c383';
        if (label === 'Free') return '#aad962';
        if (label === 'On Hold') return '#ef6a32';
        if (label === 'Occupied') return '#03c383';
        // Use palette for other labels
        return colorPalette[index % colorPalette.length];
    });
    
    // Create Chart.js doughnut chart similar to PE&P charts (using v2 syntax)
    const chartConfig = {
        type: 'doughnut',
        data: {
            labels: labels,
            datasets: [{
                data: values,
                backgroundColor: backgroundColor,
                hoverBackgroundColor: "#C0C0C0"
            }]
        },
        options: {
            layout: {
                padding: {
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0
                }
            },
            title: {
                display: true,
                text: title,
              
            },
            legend: {
                display: false
            },
            plugins: {
                labels: false // Disable chartjs-plugin-labels since we use custom plugin for all charts
            },
            cutoutPercentage: 72, // Larger center area for center legend text
            centerText: isOccupancyChart ? '' : centerText, // Only show center text for non-occupancy charts
            isOccupancyChart: isOccupancyChart, // Mark as occupancy chart to prevent showLabels plugin interference
            isInventoryChart: true // Mark as inventory chart to prevent showLabels plugin from drawing green/red center text
        }
    };
    
    // Create the chart
    window[chartKey] = new Chart(canvas, chartConfig);
    
    // Add hover tooltip for non-occupancy inventory charts (locations & value)
    // Shows label, exact value, and percentage on hover
    if (!isOccupancyChart && chartConfig.options.isInventoryChart) {
        const chart = window[chartKey];
        let tooltipDiv = null;
        const tooltipKey = `inventoryTooltip_${canvasId}`;
        
        const createTooltip = () => {
            if (tooltipDiv) return tooltipDiv;
            tooltipDiv = document.createElement('div');
            tooltipDiv.id = tooltipKey;
            tooltipDiv.style.position = 'absolute';
            tooltipDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
            tooltipDiv.style.color = 'white';
            tooltipDiv.style.padding = '8px 12px';
            tooltipDiv.style.borderRadius = '4px';
            tooltipDiv.style.fontSize = '12px';
            tooltipDiv.style.pointerEvents = 'none';
            tooltipDiv.style.zIndex = '1000';
            tooltipDiv.style.display = 'none';
            document.body.appendChild(tooltipDiv);
            return tooltipDiv;
        };
        
        const handleMouseMove = (e) => {
            const activePoints = chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true);
            
            if (activePoints.length > 0) {
                const firstPoint = activePoints[0];
                const dataset = chart.data.datasets[firstPoint._datasetIndex];
                const value = dataset.data[firstPoint._index];
                const label = chart.data.labels[firstPoint._index];
                const total = dataset.data.reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                
                const tooltip = createTooltip();
                // Show label, exact value, and percentage on hover
                tooltip.innerHTML = `${label}<br/>Value: ${value.toLocaleString()}<br/>${percentage}%`;
                tooltip.style.display = 'block';
                tooltip.style.left = (e.clientX + 10) + 'px';
                tooltip.style.top = (e.clientY + 10) + 'px';
            } else {
                if (tooltipDiv) {
                    tooltipDiv.style.display = 'none';
                }
            }
        };
        
        const handleMouseLeave = () => {
            if (tooltipDiv) {
                tooltipDiv.style.display = 'none';
            }
        };
        
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);
        
        // Store cleanup function
        window[`${chartKey}_cleanup`] = function() {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
            if (tooltipDiv && tooltipDiv.parentNode) {
                tooltipDiv.parentNode.removeChild(tooltipDiv);
            }
        };
    }
}
